import React from 'react';
import { misReportService } from '../../services/apiService';
import { useToast } from '../../components/ui/Toast';
import Button from '../../components/ui/Button';
import { useReport } from './hooks/useReport';
import ScopeBanner from './components/ScopeBanner';
import FilterBar from './components/FilterBar';
import ExecutiveSummary from './components/ExecutiveSummary';
import OperationalSection from './components/OperationalSection';
import DistributionSection from './components/DistributionSection';
import ComplianceSection from './components/ComplianceSection';

const EMPTY_FILTERS = {
  dateFrom: '',
  dateTo: '',
  status: '',
  msmeStatus: '',
  countryOrigin: '',
  region: '',
  currency: '',
};

// Drop blank values so cache keys/params stay stable and minimal.
const clean = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v != null && v !== ''));

const MisReport = () => {
  const { error: toastError, success: toastSuccess } = useToast();
  const [filters, setFilters] = React.useState(EMPTY_FILTERS);
  const [exporting, setExporting] = React.useState(false);

  const applied = React.useMemo(() => clean(filters), [filters]);

  // Summary is fetched once here and shared with the scope banner + exec section.
  const { data: summary, loading: summaryLoading } = useReport('/reports/mis/summary', applied);

  const patchFilters = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
  const resetFilters = () => setFilters(EMPTY_FILTERS);

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await misReportService.exportExcel(applied);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mis_report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toastSuccess('Report exported.');
    } catch (e) {
      console.error('MIS export failed:', e);
      toastError('Failed to export report.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="dashboard-theme flex h-full min-w-0 flex-col gap-4 overflow-auto pb-6">
      <div className="flex flex-shrink-0 items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-dash-foreground">MIS Report</h1>
          <p className="text-[11px] text-dash-muted-foreground">Management report over terminal-state vendors</p>
        </div>
        <Button variant="default" size="sm" iconName="Download" loading={exporting} onClick={handleExport}>
          Export Excel
        </Button>
      </div>

      <ScopeBanner summary={summary} />
      <FilterBar filters={filters} onChange={patchFilters} onReset={resetFilters} />

      <ExecutiveSummary summary={summary} loading={summaryLoading} filters={applied} />
      <OperationalSection filters={applied} />
      <DistributionSection filters={applied} />
      <ComplianceSection filters={applied} />
    </div>
  );
};

export default MisReport;
